import dayjs from 'dayjs';
import { Form, Spin } from 'antd';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/material';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import SectionCard from 'components/section/SectionCard';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { hasGlKey } from '../utils/helperFunc';
import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import LeaseDetailScheduleTable from './LeaseDetailScheduleTable';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';

dayjs.extend(utc);

const LeaseRecordManagementV9ScheduleDetail = ({ screen, viewData, dataFullyLoaded = false }) => {
	const { state } = useLocation();
	const form = Form.useFormInstance();
	const hasLoadedOnce = useRef(false);
	const isMountedRef = useRef(true);

	const [isClicked, setIsClicked] = useState(false);

	const profileData = useSelector((state) => state.profile?.profileData);

	const userId = profileData?.userId;
	const companyCode = profileData?.creatorOrganizationId;

	const [getLeaseTableData, { data: leaseControlDataV3, isLoading: isLeaseTableDataLoading }] = useGetQueryV3Mutation();
	const [getLeaseData, { data: leaseControlData, isLoading: isLeaseControlDataLoading }] = useSendControlDataMutation();

	const statusData = useMemo(() => {
		return Object.keys(viewData?.parkedData ?? {})?.length === 0 ? viewData?.data : viewData?.parkedData;
	}, [viewData]);

	const hasGlAssigned = hasGlKey(statusData);
	const hasCCAssigned = Boolean(statusData?.refCCAssignmentIdentityId);

	const queryV3Loader = useCallback(async () => {
		let result;

		const version = String(viewData?.data?.version);

		// if (viewData?.activationStatus?.toLowerCase() !== 'active') {
		// 	// await handleDataLoading();
		// 	return;
		// }

		if (
			version &&
			version !== 'null' &&
			version !== 'undefined' &&
			companyCode
			// &&
			// (viewData?.activationStatus?.toLowerCase() === 'active' ||
			// 	viewData?.activationStatus?.toLowerCase() === 'closed' ||
			// 	viewData?.activationStatus?.toLowerCase() === 're-open')
		) {
			result = await getLeaseTableData({
				companyCode,
				fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
				fieldValues: [companyCode, state._id, version],
				name: 'leaseSchedulesOnVersion-v9',
			});
		}

		return result;
	}, [companyCode, getLeaseTableData, state?._id, viewData?.data?.version]);

	const handleDataLoading = useCallback(async () => {
		if (hasLoadedOnce.current) return;
		hasLoadedOnce.current = true;

		setIsClicked(true);

		let payloadData = {};

		const formData = form.getFieldsValue();

		if (screen?.toLowerCase() === 'create' || (screen?.toLowerCase() === 'revise' && formData?.modificationSequence === 0)) {
			payloadData = {
				advanceAmount: formData?.advanceAmount,
				leaseTerm: formData?.leaseTerm,
				discountRate: formData?.initialDiscountRate,
				startYear: String(formData?.leaseStartDate?.year()),
				escalation: 0,
				startMonth: String(formData?.leaseStartDate?.month() + 1),
				paymentFrequency: formData?.paymentFrequency,
				paymentTime: formData?.paymentModality,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0)?.length,
				startDay: String(formData?.leaseStartDate?.date()),
				upfrontLeasePayment: String(formData?.upfrontLeasePayment),
				initialDirectCost: String(formData?.initialDirectCost),
				securityDeposit: String(formData?.securityDeposit),
				endOfFinancialYear: formData?.endOfFinancialYear,
				advanceAmounts: formData?.advanceAmounts,
				grossRents: formData?.grossRents,
				grossRent: 0,
				changes: [],
				cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
			};
			const convertJson = JSON.stringify(payloadData);

			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};
			try {
				if (Object.keys(payloadData).length !== 0) {
					await getLeaseData(formattedPayload).unwrap();
				}
			} catch (error) {
				return;
			}
		} else {
			if (dataFullyLoaded) {
				const effectiveChanges = form.getFieldValue('changes')?.filter((_, index) => index !== 0);

				const normalizedEffectiveChanges = effectiveChanges?.map((item) => {
					const grossRentsLength = item?.inputChangeGrossRents?.length || 0;
					const advanceAmountsLength = item?.inputChangeAdvanceAmounts?.length || 0;

					if (grossRentsLength > advanceAmountsLength && Array.isArray(item?.inputChangeGrossRents)) {
						const paddedAdvanceAmounts = [...(item?.inputChangeAdvanceAmounts || [])];

						while (paddedAdvanceAmounts.length < grossRentsLength) {
							paddedAdvanceAmounts.push(0);
						}
						return {
							...item,
							inputChangeAdvanceAmounts: paddedAdvanceAmounts,
						};
					}
					return item;
				});

				payloadData = {
					advanceAmount: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce(
						(accumulator, currentValue) => accumulator + currentValue,
						0
					),
					leaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
					discountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
					startDay: String(formData?.leaseStartDate?.date()),
					startMonth: String(formData?.leaseStartDate?.month() + 1),
					startYear: String(formData?.leaseStartDate?.year()),
					escalation: 0,
					paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
					paymentTime: formData?.changes?.[0]?.paymentTime,
					advanceDuration: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
					upfrontLeasePayment: formData?.changes?.[0]?.upfrontLeasePayment,
					initialDirectCost: formData?.changes?.[0]?.initialDirectCost,
					securityDeposit: formData?.securityDeposit,
					endOfFinancialYear: formData?.endOfFinancialYear,
					advanceAmounts: formData?.changes?.[0]?.inputChangeAdvanceAmounts,
					grossRents: formData?.changes?.[0]?.inputChangeGrossRents,
					cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
					changes: normalizedEffectiveChanges?.map((item, index) => {
						if (index === normalizedEffectiveChanges.length - 1) {
							return {
								...item,
								inputChangeAdvance: formData?.advanceAmounts?.reduce((acc, curr) => acc + curr, 0),
								inputChangeAdvanceAmounts: formData?.advanceAmounts,
								inputChangeGrossRents: formData?.grossRents,
								upfrontLeasePayment: String(formData?.upfrontLeasePayment),
								initialDirectCost: String(formData?.initialDirectCost),
								securityDeposit: String(formData?.securityDeposit),
								cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
								inputChangeDiscountRate: formData?.initialDiscountRate,
							};
						}
						return {
							...item,
						};
					}),
					grossRent: 0,
				};

				if (
					Array.isArray(payloadData.grossRents) &&
					Array.isArray(payloadData.advanceAmounts) &&
					payloadData.grossRents.length > payloadData.advanceAmounts.length
				) {
					const paddedAdvanceAmounts = [...payloadData.advanceAmounts];
					while (paddedAdvanceAmounts.length < payloadData.grossRents.length) {
						paddedAdvanceAmounts.push(0);
					}
					payloadData.advanceAmounts = paddedAdvanceAmounts;
				}
			}

			const convertJson = JSON.stringify(payloadData);

			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};

			try {
				if (Object.keys(payloadData).length !== 0) {
					await getLeaseData(formattedPayload).unwrap();
				}
			} catch (error) {
				console.error('Error in data loading:', error);
			}
		}
	}, [dataFullyLoaded, form, getLeaseData, screen, state?.processCode]);

	const filteredDataForTable = useMemo(() => {
		if (Array.isArray(leaseControlDataV3?.results)) {
			return leaseControlDataV3.results;
		}

		if (Array.isArray(leaseControlData?.responseData)) {
			return leaseControlData.responseData;
		}

		return [];
	}, [leaseControlData?.responseData, leaseControlDataV3?.results]);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		setIsClicked(false);
	}, [viewData?.data?.leaseId]);

	useEffect(() => {
		if (viewData?.activationStatus === 'W-I-P' && viewData?.approverUserId?.includes(userId)) {
			handleDataLoading();
		} else if (viewData?.activationStatus === 'W-I-P' && viewData?.creatorId === userId && viewData?.history?.length > 2) {
			handleDataLoading();
		} else {
			queryV3Loader();
		}
	}, [handleDataLoading, queryV3Loader, userId, viewData?.activationStatus, viewData?.approverUserId, viewData?.creatorId, viewData?.history?.length]);

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '4px', flexDirection: 'column' }}>
			<Spin spinning={isLeaseControlDataLoading}>
				<Spin spinning={isLeaseTableDataLoading || isLeaseControlDataLoading}>
					{filteredDataForTable?.length > 0 && (
						<SectionCard
							sx={{
								padding: '16px',
								borderRadius: '10px',
								border: '1px solid #e6ebf2',
							}}
						>
							<LeaseDetailScheduleTable
								screen={screen}
								isClicked={isClicked}
								viewData={statusData}
								queryV3Loader={queryV3Loader}
								hasGlAssigned={hasGlAssigned}
								hasCCAssigned={hasCCAssigned}
								leaseControlData={filteredDataForTable}
							/>
						</SectionCard>
					)}
				</Spin>
			</Spin>
		</Box>
	);
};

export default LeaseRecordManagementV9ScheduleDetail;
